import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState, Avatar } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Trophy, CheckCircle2, Star } from "lucide-react";

export default function Chores() {
  const [chores, setChores] = useState([]);
  const [members, setMembers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", points: 10, assigned_to: "" });

  const load = async () => {
    const [c, m, r] = await Promise.all([api.get("/api/chores"), api.get("/api/members"), api.get("/api/rewards")]);
    setChores(c.data);
    setMembers(m.data);
    setRewards(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/chores", form);
      toast.success("Chore added!");
      setOpen(false);
      setForm({ title: "", description: "", points: 10, assigned_to: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const complete = async (id) => {
    await api.patch(`/api/chores/${id}/complete`);
    toast.success("Chore done! Points awarded.");
    load();
  };

  const remove = async (id) => { await api.delete(`/api/chores/${id}`); load(); };

  const memberById = (id) => members.find((m) => m.id === id);
  const pointsFor = (id) => rewards.find((r) => r.member_id === id)?.points || 0;
  const sortedRewards = [...members].map((m) => ({ ...m, points: pointsFor(m.id) })).sort((a, b) => b.points - a.points);
  const open_chores = chores.filter((c) => !c.completed);
  const completed_chores = chores.filter((c) => c.completed);

  return (
    <div data-testid="chores-page">
      <PageHeader
        title="Chores & Rewards"
        subtitle="Earn points by completing chores. Family leaderboard included."
        color="#A8BFD1"
        action={<NBButton testId="add-chore-btn" color="#A8BFD1" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Chore</NBButton>}
      />

      {/* Leaderboard */}
      <NBCard color="#E8B96B" className="mb-6" hover={false}>
        <div className="flex items-center gap-2 mb-3">
          <Trophy strokeWidth={3} />
          <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Leaderboard</h3>
        </div>
        <div className="space-y-2">
          {sortedRewards.map((m, i) => (
            <div key={m.id} className="flex items-center gap-3 p-2 bg-white border-2 border-black rounded-lg" data-testid={`leaderboard-${m.id}`}>
              <span className="text-2xl font-black w-8" style={{ fontFamily: "var(--hh-display)" }}>#{i + 1}</span>
              <Avatar name={m.name} color={m.avatar_color} size={32} />
              <span className="flex-1 font-bold">{m.name}</span>
              <span className="flex items-center gap-1 font-black" style={{ fontFamily: "var(--hh-display)" }}>
                <Star strokeWidth={3} className="w-4 h-4 fill-[#E8B96B]" /> {m.points}
              </span>
            </div>
          ))}
        </div>
      </NBCard>

      <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>Open Chores</h3>
      {open_chores.length === 0 ? (
        <EmptyState title="All clear!" subtitle="Add chores to earn points." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {open_chores.map((c) => {
            const m = memberById(c.assigned_to);
            return (
              <NBCard key={c.id} data-testid={`chore-${c.id}`}>
                <div className="flex justify-between">
                  <span className="nb-badge bg-[#E8B96B] flex items-center gap-1"><Star strokeWidth={3} className="w-3 h-3" /> {c.points}</span>
                  <button onClick={() => remove(c.id)} className="nb-btn bg-[#E9B5A0] p-1.5">
                    <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                  </button>
                </div>
                <h4 className="text-lg font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{c.title}</h4>
                {c.description && <p className="text-sm mt-1">{c.description}</p>}
                {m && (
                  <div className="flex items-center gap-2 mt-3">
                    <Avatar name={m.name} color={m.avatar_color} size={24} />
                    <span className="text-xs font-bold">{m.name}</span>
                  </div>
                )}
                <button onClick={() => complete(c.id)} data-testid={`complete-chore-${c.id}`} className="nb-btn bg-[#B8CFA9] mt-3 w-full px-3 py-2 text-sm">
                  <CheckCircle2 strokeWidth={2.5} className="w-4 h-4 inline mr-1" /> Complete
                </button>
              </NBCard>
            );
          })}
        </div>
      )}

      {completed_chores.length > 0 && (
        <>
          <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>Completed</h3>
          <div className="space-y-2">
            {completed_chores.slice(0, 10).map((c) => (
              <div key={c.id} className="nb-card !p-3 flex items-center gap-3 opacity-70">
                <CheckCircle2 strokeWidth={2.5} className="text-emerald-700 shrink-0" />
                <span className="flex-1 font-bold line-through">{c.title}</span>
                <span className="font-black text-sm" style={{ fontFamily: "var(--hh-display)" }}>+{c.points} pts</span>
              </div>
            ))}
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Chore</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="chore-title" required placeholder="e.g. Take out trash" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBTextarea testId="chore-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput testId="chore-points" type="number" min={1} placeholder="Points" value={form.points} onChange={(e) => setForm({ ...form, points: parseInt(e.target.value) || 0 })} />
              <NBSelect testId="chore-assigned" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Anyone</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </NBSelect>
              <NBButton testId="submit-chore" type="submit" color="#A8BFD1" className="w-full">Save Chore</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
